<template>
  <div class="office-timelog p-3">
    <b-row align-v="center" class="loader" v-if="is_saving">
      <b-col class="text-center">
        <b-spinner></b-spinner>
        <div class="mt-2"><strong>Loading</strong></div>
      </b-col>
    </b-row>

    <!-- ================= Checking stored device token ================= -->
    <div v-if="kiosk_state === 'checking'" class="text-center py-5">
      <b-spinner></b-spinner>
      <div class="mt-2"><strong>Checking kiosk activation...</strong></div>
    </div>

    <!-- ================= Activation screen (no device token) ================= -->
    <b-row v-else-if="kiosk_state === 'inactive'" align-h="center" class="pt-4">
      <b-col md="6" lg="5">
        <div class="card shadow border-0">
          <div class="card-header bg-dark text-white py-3 text-center">
            <font-awesome-icon icon="lock" class="h3 mb-1" />
            <h5 class="mb-0 font-weight-bold">Kiosk Activation</h5>
            <small class="text-white-50">This machine is not yet allowed to record time logs.</small>
          </div>
          <div class="card-body">
            <b-alert :show="!!activation_message" :variant="activation_variant" class="py-2">
              {{ activation_message }}
            </b-alert>

            <p class="text-muted small mb-3">
              Ask an administrator to generate a <strong>6-digit activation code</strong> from
              <em>Configuration &rsaquo; Kiosk Devices</em>. The code is valid for 3 minutes and can be used on one machine only.
            </p>

            <b-form @submit.prevent="activate">
              <b-form-group label="Activation code">
                <b-form-input
                  v-model="activation.code"
                  ref="code"
                  autofocus
                  required
                  autocomplete="one-time-code"
                  inputmode="numeric"
                  maxlength="6"
                  size="lg"
                  class="text-center kiosk-code-input"
                  placeholder="••••••"
                  :disabled="activating"
                  @input="onCodeInput"
                ></b-form-input>
              </b-form-group>
              <b-form-group label="Name this machine" description="Shown to administrators so they can recognize and manage this kiosk.">
                <b-form-input
                  v-model="activation.device_name"
                  required
                  maxlength="100"
                  autocomplete="off"
                  placeholder="e.g. Front Desk PC"
                  :disabled="activating"
                ></b-form-input>
              </b-form-group>
              <b-button type="submit" variant="success" block size="lg" :disabled="activating || activation.code.length !== 6">
                <b-spinner small v-if="activating"></b-spinner>
                <font-awesome-icon icon="check-circle" v-else />
                Activate this machine
              </b-button>
            </b-form>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ================= Kiosk (activated) ================= -->
    <div v-else>
      <!-- Today's holiday status -->
      <div
        class="rounded shadow-sm mb-3 px-3 py-2 d-flex align-items-center justify-content-between text-white"
        :class="holiday ? (holiday.type == 1 ? 'bg-success' : 'bg-warning') : 'bg-info'"
      >
        <div class="d-flex align-items-center">
          <font-awesome-icon :icon="holiday ? 'calendar' : 'check'" class="mr-2" />
          <strong v-if="holiday">
            {{ holiday.type_description }} &mdash; {{ holiday.name }}
            <span class="text-white-50">({{ holiday.type == 1 ? '1 day holiday pay + duty if worked' : 'duty + 30% if worked' }})</span>
          </strong>
          <strong v-else>Regular working day</strong>
        </div>
        <b-badge variant="light" class="text-dark" v-if="device">
          <font-awesome-icon icon="qrcode" /> {{ device.name }}
        </b-badge>
      </div>

      <b-row>
        <!-- Left: date, clock, mode, scan -->
        <b-col lg="5" class="mb-3">
          <b-row class="m-0 mb-3">
            <b-col cols="6" class="p-0 pr-2">
              <div class="rounded overflow-hidden shadow-sm h-100 d-flex">
                <div class="bg-facebook text-white d-flex align-items-center justify-content-center px-3">
                  <font-awesome-icon icon="calendar" class="h4 mb-0" />
                </div>
                <div class="custom-background-blue text-white px-3 py-3 flex-grow-1">
                  <h5 class="mb-0"><strong>{{ date }}</strong></h5>
                  <small class="text-white-50 text-uppercase font-weight-bold">Date</small>
                </div>
              </div>
            </b-col>
            <b-col cols="6" class="p-0">
              <div class="rounded overflow-hidden shadow-sm h-100 d-flex">
                <div class="bg-facebook text-white d-flex align-items-center justify-content-center px-3">
                  <font-awesome-icon icon="clock" class="h4 mb-0" />
                </div>
                <div class="custom-background-blue text-white px-3 py-2 flex-grow-1 text-center d-flex align-items-center justify-content-center">
                  <vue-clock />
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Mode selector -->
          <div class="card shadow-sm border-0 mb-3">
            <div class="card-header bg-dark text-white py-2">
              <font-awesome-icon icon="cog" /> Select Mode
            </div>
            <div class="card-body p-2">
              <b-row class="m-0">
                <b-col
                  v-for="m in visibleModes"
                  :key="m.value"
                  cols="4"
                  class="p-1"
                >
                  <b-button
                    block
                    size="sm"
                    :variant="mode === m.value ? m.variant : 'outline-secondary'"
                    class="py-2 font-weight-bold"
                    :disabled="!modeEnabled(m.value)"
                    @click="selectMode(m.value)"
                  >
                    {{ m.label }}
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </div>

          <!-- Scheduled OT Permission for today, per payroll group -->
          <div class="card shadow-sm border-0 mb-3" v-if="otWindows.length">
            <div
              class="card-header text-white py-2 d-flex align-items-center justify-content-between"
              :class="anyOtOpen ? 'bg-success' : (otWindows.some(w => w.state === 'upcoming') ? 'bg-info' : 'bg-secondary')"
            >
              <span><font-awesome-icon icon="sign-out-alt" /> Scheduled OT today</span>
              <b-badge variant="light" class="text-dark">{{ otWindows.length }} schedule(s)</b-badge>
            </div>
            <div class="card-body p-0">
              <table class="table table-sm mb-0">
                <tbody>
                  <tr v-for="w in otWindows" :key="w.schedule.id">
                    <th class="pl-3 align-middle">
                      <b-badge v-for="s in (w.schedule.shift_types || [])" :key="s.id" variant="dark" class="mr-1 mb-1 font-weight-normal">
                        <span class="text-white-50">{{ s.payroll_group ? s.payroll_group.name : '-' }} &middot;</span> {{ s.description }}
                      </b-badge>
                      <div class="small text-muted font-weight-normal">{{ w.schedule.purpose }}</div>
                    </th>
                    <td class="align-middle text-center text-nowrap">{{ w.schedule.formatted_time_from }} &ndash; {{ w.schedule.formatted_time_to }}</td>
                    <td class="align-middle text-right pr-3">
                      <b-badge :variant="w.state === 'open' ? 'success' : (w.state === 'upcoming' ? 'info' : 'secondary')">
                        <template v-if="w.state === 'open'">open &middot; closes in {{ w.countdown }}</template>
                        <template v-else-if="w.state === 'upcoming'">opens in {{ w.countdown }}</template>
                        <template v-else>closed &middot; OT OUT still allowed</template>
                      </b-badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else class="alert alert-light border small py-2 mb-3 text-muted">
            <font-awesome-icon icon="info-circle" /> No scheduled OT today &mdash; OT IN / OT OUT are hidden.
          </div>

          <!-- Scan -->
          <div class="card shadow-sm border-0">
            <div class="card-header bg-success text-white py-2 d-flex align-items-center justify-content-between">
              <span><font-awesome-icon icon="qrcode" /> Scan QR / Enter ID</span>
              <b-badge :variant="currentMode.variant">{{ currentMode.label }}</b-badge>
            </div>
            <div class="card-body">
              <b-form @submit.prevent="save">
                <b-form-input
                  v-model="form.id_number"
                  type="password"
                  ref="scan"
                  autofocus
                  required
                  autocomplete="off"
                  size="lg"
                  class="text-center"
                  placeholder="Waiting for scan..."
                ></b-form-input>
                <small class="text-muted text-uppercase font-weight-bold d-block mt-2 text-center">
                  Scan a QR or type the ID number, then press Enter
                </small>
              </b-form>
            </div>
          </div>

          <!-- Active shift schedules -->
          <div class="card shadow-sm border-0 mt-3" v-if="shifts.length">
            <div class="card-header bg-dark text-white py-2 d-flex align-items-center justify-content-between">
              <span><font-awesome-icon icon="cog" /> Today's Shift Schedules</span>
              <small class="text-white-50">rules follow the scanned employee's shift</small>
            </div>
            <div class="card-body p-0">
              <table class="table table-sm mb-0 small">
                <tbody>
                  <template v-for="s in shifts">
                    <tr :key="'h' + s.id" class="table-light">
                      <th colspan="3" class="pl-3">
                        {{ s.payroll_group }} &middot; {{ s.description }}
                        <b-badge variant="dark" class="ml-1" v-if="s.version">{{ hoursText(s.version.required_duty_hours) }} duty</b-badge>
                        <small class="text-muted float-right pr-2" v-if="s.version && s.version.formatted_effective_date">
                          since {{ s.version.formatted_effective_date.other_formats.format_3 }}
                        </small>
                      </th>
                    </tr>
                    <tr :key="'a' + s.id" v-if="s.version">
                      <th class="pl-3"><font-awesome-icon icon="sign-in-alt" class="text-success" /> AM</th>
                      <td class="text-center">{{ hm(s.version.am_time_in) }} &ndash; {{ hm(s.version.am_time_out) }}</td>
                      <td class="text-right pr-3">
                        <b-badge variant="light">grace {{ s.version.am_time_in_grace }}m</b-badge>
                        <b-badge v-if="Number(s.version.am_in_strict) === 1" variant="danger" title="AM IN closes when the grace ends">strict</b-badge>
                      </td>
                    </tr>
                    <tr :key="'p' + s.id" v-if="s.version">
                      <th class="pl-3"><font-awesome-icon icon="sign-in-alt" class="text-success" /> PM</th>
                      <td class="text-center">{{ hm(s.version.pm_time_in) }} &ndash; {{ hm(s.version.pm_time_out) }}</td>
                      <td class="text-right pr-3">
                        <b-badge variant="light">grace {{ s.version.pm_time_in_grace }}m</b-badge>
                        <b-badge v-if="Number(s.version.pm_in_strict) === 1" variant="danger" title="PM IN closes when the grace ends">strict</b-badge>
                      </td>
                    </tr>
                    <tr :key="'o' + s.id" v-if="s.version">
                      <th class="pl-3"><font-awesome-icon icon="clock" /> Rules</th>
                      <td class="text-center" colspan="2">
                        PM IN opens {{ s.version.early_scan_minutes }} min before {{ hm(s.version.pm_time_in) }}
                        &middot; OT rate {{ otRateText(s.version) }}
                        <template v-if="Number(s.version.am_extension_enabled) === 1">
                          &middot; AM extension from {{ s.version.am_extension_min_minutes }} min after {{ hm(s.version.am_time_out) }} (max {{ s.version.am_extension_max_minutes }} min)
                        </template>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </b-col>

        <!-- Right: camera + recent logs -->
        <b-col lg="7">
          <div class="card shadow-sm border-0 mb-3">
            <div class="card-header bg-dark text-white py-2">
              <font-awesome-icon icon="qrcode" /> Camera
            </div>
            <div class="card-body p-2 custom-background-blue text-center">
              <video v-show="isCameraOpen" ref="camera" autoplay width="100%" class="rounded"></video>
              <canvas v-show="false" ref="canvas" :width="650" :height="488"></canvas>
              <div v-if="!isCameraOpen" class="text-white py-4">
                <template v-if="camera_error">
                  <font-awesome-icon icon="info-circle" class="text-warning" />
                  <div class="small mt-1 px-3">{{ camera_error }}</div>
                </template>
                <template v-else>
                  <b-spinner small></b-spinner> Starting camera...
                </template>
              </div>
            </div>
          </div>

          <office-timelog-table
            ref="displayTable"
            api_url="office-kiosk/recent"
            :kiosk="true"
            @unauthorized="onKioskUnauthorized"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  import SwalMixin from '@/views/mixins/Swal.js'
  import OfficeTimelogTable from './OfficeTimelogTable.vue'
  import {
    kioskAxios,
    getKioskToken,
    getKioskDevice,
    setKioskSession,
    clearKioskSession,
    deviceFingerprint,
    defaultDeviceName,
    isKioskUnauthorized
  } from '@/kiosk'

  export default {
    name: 'OfficeTimelog',
    mixins: [SwalMixin],
    components: { OfficeTimelogTable },
    data() {
      return {
        // 'checking' -> verifying a stored device token
        // 'inactive' -> no valid token, show activation screen
        // 'active'   -> kiosk ready
        kiosk_state: 'checking',
        device: null,
        activation: { code: '', device_name: defaultDeviceName() },
        activating: false,
        activation_message: '',
        activation_variant: 'info',

        date: '',
        is_saving: false,
        mode: 1,
        modes: [
          { value: 1, label: 'AM IN', variant: 'success' },
          { value: 2, label: 'AM OUT', variant: 'warning' },
          { value: 3, label: 'PM IN', variant: 'success' },
          { value: 4, label: 'PM OUT', variant: 'warning' },
          { value: 5, label: 'OT IN', variant: 'info' },
          { value: 6, label: 'OT OUT', variant: 'danger' },
        ],
        // The camera is always on: a photo is saved with every punch.
        capture_image: true,
        // Active shifts of active payroll groups with today's schedule version.
        shifts: [],
        holiday: null,
        // Scheduled OT Permissions for today, each naming the shifts it covers (empty = OT buttons hidden).
        ot_schedules: [],
        now: Date.now(),
        tick_timer: null,
        ot_timer: null,
        isCameraOpen: false,
        // Set when the camera cannot start; shown in the camera panel instead
        // of a blocking alert, so scanning continues without a photo.
        camera_error: '',
        form: {
          id_number: '',
          mode: 1,
          image: ''
        }
      }
    },
    computed: {
      currentMode() {
        return this.modes.find(m => m.value === this.mode) || this.modes[0];
      },
      // OT IN / OT OUT exist only on a day with a Scheduled OT Permission for
      // at least one shift. The server checks the scanned employee's own shift.
      visibleModes() {
        return this.modes.filter(m => (m.value !== 5 && m.value !== 6) || this.ot_schedules.length > 0);
      },
      // Each schedule's OT window with its state: upcoming | open | closed.| open | closed.
      otWindows() {
        return this.ot_schedules.map(s => {
          const base = (s.date || '').substring(0, 10);
          const from = new Date(base + 'T' + s.time_from);
          let to = new Date(base + 'T' + s.time_to);
          if (to <= from) to = new Date(to.getTime() + 24 * 3600 * 1000);
          let state = 'open';
          if (this.now < from.getTime()) state = 'upcoming';
          else if (this.now > to.getTime()) state = 'closed';
          const target = state === 'upcoming' ? from.getTime() : to.getTime();
          const minutes = Math.max(0, Math.round((target - this.now) / 60000));
          const h = Math.floor(minutes / 60), m = minutes % 60;
          return { schedule: s, from, to, state, countdown: (h ? h + 'h ' : '') + m + 'm' };
        });
      },
      anyOtOpen() {
        return this.otWindows.some(w => w.state === 'open');
      },
      anyOtOpenOrClosed() {
        return this.otWindows.some(w => w.state === 'open' || w.state === 'closed');
      }
    },
    watch: {
      // If the OT schedule disappears while an OT mode is selected, fall back to AM IN.
      visibleModes(list) {
        if (!list.find(m => m.value === this.mode)) this.mode = 1;
      }
    },
    created() {
      this.date = new Date().toDateString();
      this.checkActivation();
    },
    beforeDestroy() {
      this.stopCamera();
      this.stopTimers();
    },
    methods: {
      /* ---------------- activation ---------------- */
      checkActivation() {
        if (!getKioskToken()) {
          this.kiosk_state = 'inactive';
          return;
        }
        this.kiosk_state = 'checking';
        kioskAxios.get('office-kiosk/me').then(response => {
          this.device = response.data;
          setKioskSession(getKioskToken(), this.device);
          this.startKiosk();
        }).catch(error => {
          if (isKioskUnauthorized(error)) {
            this.deactivate((error.response && error.response.data) ? error.response.data : 'This kiosk is no longer activated.', 'warning');
          } else {
            // Server unreachable: keep the token and let the kiosk try to work.
            this.device = getKioskDevice();
            this.startKiosk();
          }
        });
      },
      onCodeInput(value) {
        this.activation.code = (value || '').replace(/\D/g, '').substring(0, 6);
      },
      activate() {
        if (this.activation.code.length !== 6 || this.activating) return;
        this.activating = true;
        this.activation_message = '';
        kioskAxios.post('office-kiosk/activate', {
          code: this.activation.code,
          device_name: this.activation.device_name,
          fingerprint: deviceFingerprint()
        }).then(response => {
          this.activating = false;
          this.device = response.data.device;
          setKioskSession(response.data.token, this.device);
          this.activation.code = '';
          this.swal_transaction_completed_data.text = 'Kiosk activated as "' + this.device.name + '".';
          this.swalTransactionCompleted();
          this.startKiosk();
        }).catch(error => {
          this.activating = false;
          this.activation.code = '';
          if (error.response && error.response.status === 429) {
            this.activation_message = 'Too many attempts. Please wait a minute and try again.';
          } else {
            this.activation_message = (error.response && error.response.data) ? error.response.data : 'Activation failed. Please try again.';
          }
          this.activation_variant = 'danger';
          this.$nextTick(() => { if (this.$refs.code) this.$refs.code.focus(); });
        });
      },
      deactivate(message, variant) {
        clearKioskSession();
        this.stopCamera();
        this.stopTimers();
        this.ot_schedules = [];
        this.device = null;
        this.shifts = [];
        this.camera_error = '';
        this.holiday = null;
        this.activation_message = message || '';
        this.activation_variant = variant || 'warning';
        this.kiosk_state = 'inactive';
      },
      onKioskUnauthorized(error) {
        this.deactivate((error && error.response && error.response.data) ? error.response.data : 'This kiosk has been terminated by an administrator.', 'warning');
      },
      startKiosk() {
        this.kiosk_state = 'active';
        this.getSettings();
        this.getHoliday();
        this.getOtSchedule();
        this.startTimers();
        this.focusScan();
        // Camera last and isolated: scanning, schedules and the OT card must
        // load even where the camera is unavailable (see openCamera).
        this.openCamera();
      },
      getOtSchedule() {
        kioskAxios.get('office-kiosk/ot-schedule').then(response => {
          const d = response.data;
          this.ot_schedules = Array.isArray(d) ? d : (d ? [d] : []);
        }).catch(error => {
          if (isKioskUnauthorized(error)) return this.onKioskUnauthorized(error);
          console.log(error);
        });
      },
      // OT IN only while some group's window is open; OT OUT from a window
      // start onward (an employee may still leave after the window closes).
      // The server enforces the scanned employee's own shift.
      modeEnabled(value) {
        if (value === 5) return this.anyOtOpen;
        if (value === 6) return this.anyOtOpenOrClosed;
        return true;
      },
      startTimers() {
        this.stopTimers();
        this.tick_timer = setInterval(() => { this.now = Date.now(); }, 1000);
        // Re-check the schedule every minute so a newly scheduled OT (or a
        // new day) shows up without reloading the kiosk.
        this.ot_timer = setInterval(() => { this.getOtSchedule(); this.getHoliday(); }, 60000);
      },
      stopTimers() {
        clearInterval(this.tick_timer);
        clearInterval(this.ot_timer);
        this.tick_timer = null;
        this.ot_timer = null;
      },

      /* ---------------- kiosk ---------------- */
      hm(value) {
        if (!value) return '--';
        const parts = value.split(':');
        let h = parseInt(parts[0], 10);
        const m = parts.length > 1 ? parts[1] : '00';
        const ampm = h >= 12 ? 'pm' : 'am';
        h = h % 12;
        if (h === 0) h = 12;
        return h + ':' + m + ' ' + ampm;
      },
      hoursText(value) {
        const n = Number(value);
        if (isNaN(n)) return '--';
        return (Math.round(n * 100) / 100).toString() + 'h';
      },
      otRateText(version) {
        if (!version) return '--';
        if (Number(version.ot_multiplier_enabled) === 0) return '100% (no premium)';
        return (100 + Number(version.ot_multiplier_percent || 0)) + '%';
      },
      getHoliday() {
        kioskAxios.get('office-kiosk/holiday').then(response => {
          this.holiday = response.data || null;
        }).catch(error => {
          if (isKioskUnauthorized(error)) return this.onKioskUnauthorized(error);
          console.log(error);
        });
      },
      getSettings() {
        // Every active shift with the schedule version in effect for today (not
        // the working row), so a change made today only affects the kiosk tomorrow.
        kioskAxios.get('office-kiosk/settings').then(response => {
          const d = response.data || {};
          this.shifts = d.shifts || [];
        }).catch(error => {
          if (isKioskUnauthorized(error)) return this.onKioskUnauthorized(error);
          console.log(error);
        });
      },
      // navigator.mediaDevices exists only in a secure context (HTTPS, or
      // localhost during development). On a kiosk served over plain HTTP it is
      // undefined, which used to throw and abort startKiosk() - taking the
      // holiday banner, the OT card, the shift schedules and the timers with
      // it. A camera problem must never do more than disable the photo.
      openCamera() {
        try {
          if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.camera_error = (window.isSecureContext === false)
              ? 'Camera unavailable: this kiosk is not served over HTTPS. Punches are still recorded, without a photo.'
              : 'This browser cannot access a camera. Punches are still recorded, without a photo.';
            return;
          }
          navigator.mediaDevices.getUserMedia({ audio: false, video: true }).then(stream => {
            this.camera_stream = stream;
            this.isCameraOpen = true;
            this.camera_error = '';
            this.$nextTick(() => {
              if (this.$refs.camera) this.$refs.camera.srcObject = stream;
            });
          }).catch(() => {
            this.camera_error = 'Unable to access the camera. Check the device or the browser permission. Punches are still recorded, without a photo.';
          });
        } catch (e) {
          this.camera_error = 'Unable to start the camera. Punches are still recorded, without a photo.';
        }
      },
      stopCamera() {
        if (this.camera_stream) {
          this.camera_stream.getTracks().forEach(track => track.stop());
          this.camera_stream = null;
        }
        this.isCameraOpen = false;
      },
      takePhoto() {
        if (!this.capture_image || !this.isCameraOpen || !this.$refs.canvas || !this.$refs.camera) return '';
        const canvas = this.$refs.canvas;
        const context = canvas.getContext('2d');
        context.drawImage(this.$refs.camera, 0, 0, canvas.width, canvas.height);
        context.fillStyle = 'yellow';
        context.fillText(new Date().toString(), 10, canvas.height - 12);
        return canvas.toDataURL('image/jpeg');
      },
      selectMode(value) {
        this.mode = value;
        this.focusScan();
      },
      focusScan() {
        this.$nextTick(() => {
          if (this.$refs.scan) this.$refs.scan.focus();
        });
      },
      save() {
        if (this.form.id_number === '') return;
        this.form.mode = this.mode;
        this.form.image = this.capture_image ? this.takePhoto() : '';
        this.is_saving = true;
        kioskAxios.post('office-kiosk/save', this.form).then(() => {
          this.is_saving = false;
          if (this.$refs.displayTable) this.$refs.displayTable.getAll();
          this.swal_transaction_completed_data.text = this.currentMode.label + ' recorded.';
          this.swalTransactionCompleted();
          this.form.id_number = '';
          this.focusScan();
        }).catch(error => {
          this.is_saving = false;
          this.form.id_number = '';
          if (isKioskUnauthorized(error)) {
            return this.onKioskUnauthorized(error);
          }
          this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Something went wrong.';
          this.swalRequestError();
          this.focusScan();
        });
      }
    }
  }
</script>

<style scoped>
  .office-timelog {
    min-height: 100%;
  }
  .kiosk-code-input {
    font-size: 2.2rem;
    letter-spacing: 0.6rem;
    font-weight: 700;
    height: auto;
  }
</style>
